import type { BookSalesResponse } from "reedy-data/api";
import type {
  CurrencyTotal,
  FormatMeta,
  MarketMeta,
  MarketTotal,
  SalesBucketDetail,
  SalesBucketMarketDetail,
  SalesChartDatum,
  TotalSalesViewModel,
} from "./totalSalesViewModel";

export type * from "./totalSalesViewModel";

const toCurrencyTotals = (map: Map<string, number>): CurrencyTotal[] =>
  [...map.entries()].map(([currency, revenue]) => ({ currency, revenue }));

// Home market first per sales/info order; drop markets absent from the window.
const orderedMarkets = (
  responseMarkets: MarketMeta[],
  marketOrder?: string[],
): MarketMeta[] => {
  const marketByCode = new Map(
    responseMarkets.map((meta) => [meta.market, meta]),
  );
  const order = marketOrder?.length
    ? marketOrder
    : responseMarkets.map((meta) => meta.market);
  return order
    .map((code) => marketByCode.get(code))
    .filter((meta): meta is MarketMeta => meta !== undefined);
};

const mapMarketResponse = (
  sales: Pick<BookSalesResponse, "markets" | "buckets" | "peak">,
  marketOrder?: string[],
): TotalSalesViewModel => {
  const { buckets } = sales;
  const markets = orderedMarkets(sales.markets, marketOrder);

  const data: SalesChartDatum[] = buckets.map((bucket) => {
    const detail: Record<string, SalesBucketDetail> = {};
    const datum: SalesChartDatum = { date: bucket.date, total: 0, detail };
    let total = 0;
    markets.forEach((meta) => {
      const value = bucket.byMarket[meta.market];
      const units = value?.units ?? 0;
      datum[meta.market] = units;
      detail[meta.market] = {
        units,
        revenue: value?.revenue ?? null,
        currency: meta.currency,
      };
      total += units;
    });
    datum.total = total;
    return datum;
  });

  const byMarket: MarketTotal[] = markets.map((meta) => {
    let units = 0;
    let revenue = 0;
    let hasRevenue = false;
    buckets.forEach((bucket) => {
      const value = bucket.byMarket[meta.market];
      if (!value) {
        return;
      }
      units += value.units;
      if (value.revenue != null) {
        revenue += value.revenue;
        hasRevenue = true;
      }
    });
    return {
      market: meta.market,
      units,
      revenue: hasRevenue ? revenue : null,
      currency: meta.currency,
    };
  });

  const revenueByCurrencyMap = new Map<string, number>();
  byMarket.forEach((market) => {
    if (market.revenue == null) {
      return;
    }
    revenueByCurrencyMap.set(
      market.currency,
      (revenueByCurrencyMap.get(market.currency) ?? 0) + market.revenue,
    );
  });

  const units = byMarket.reduce((sum, market) => sum + market.units, 0);

  return {
    data,
    markets,
    stackMode: markets.length > 1 ? "market" : "single",
    totals: {
      units,
      revenueByCurrency: toCurrencyTotals(revenueByCurrencyMap),
      byMarket,
    },
    peak: sales.peak,
  };
};

const mapFormatResponse = (
  sales: Pick<BookSalesResponse, "markets" | "buckets">,
  formats: FormatMeta[],
  marketOrder?: string[],
): TotalSalesViewModel => {
  const { buckets } = sales;
  const markets = orderedMarkets(sales.markets, marketOrder);
  const currencyByMarket = new Map(
    sales.markets.map((meta) => [meta.market, meta.currency]),
  );

  const data: SalesChartDatum[] = buckets.map((bucket) => {
    const detail: Record<string, SalesBucketDetail> = {};
    const datum: SalesChartDatum = { date: bucket.date, total: 0, detail };
    let total = 0;
    formats.forEach((format) => {
      const sourceByMarket = bucket.byIsbn?.[format.isbn]?.byMarket;
      let units = 0;
      const byMarket: Record<string, SalesBucketMarketDetail> = {};
      if (sourceByMarket) {
        Object.values(sourceByMarket).forEach((value) => {
          units += value.units;
        });
      }
      markets.forEach((meta) => {
        const value = sourceByMarket?.[meta.market];
        byMarket[meta.market] = {
          units: value?.units ?? 0,
          revenue: value?.revenue ?? null,
          currency: meta.currency,
        };
      });
      datum[format.isbn] = units;
      detail[format.isbn] = { units, revenue: null, currency: "", byMarket };
      total += units;
    });
    datum.total = total;
    return datum;
  });

  const revenueByCurrencyMap = new Map<string, number>();
  buckets.forEach((bucket) => {
    formats.forEach((format) => {
      const sourceByMarket = bucket.byIsbn?.[format.isbn]?.byMarket;
      if (!sourceByMarket) {
        return;
      }
      Object.entries(sourceByMarket).forEach(([market, value]) => {
        if (value.revenue == null) {
          return;
        }
        const currency = currencyByMarket.get(market) ?? market;
        revenueByCurrencyMap.set(
          currency,
          (revenueByCurrencyMap.get(currency) ?? 0) + value.revenue,
        );
      });
    });
  });

  const units = data.reduce((sum, datum) => sum + datum.total, 0);

  return {
    data,
    markets,
    formats,
    stackMode: "format",
    totals: {
      units,
      revenueByCurrency: toCurrencyTotals(revenueByCurrencyMap),
      byMarket: [],
    },
  };
};

export const mapSalesResponse = (
  sales: Pick<BookSalesResponse, "markets" | "buckets" | "peak">,
  marketOrder?: string[],
  formats?: FormatMeta[],
): TotalSalesViewModel => {
  if (formats?.length) {
    return mapFormatResponse(sales, formats, marketOrder);
  }
  return mapMarketResponse(sales, marketOrder);
};
